import { Button, Spinner } from '../ui';
import type { AnalysisJob, ConversationAnalysisResult } from '../../types';
import { CollapsibleSection, EvidenceSnippets } from './helpers';
import {
  STEP_LABELS,
  STAGE_LABELS,
  OWNER_LABELS,
  RISK_SEVERITY_COLORS,
  DISCUSSED_TYPE_LABELS,
  formatTimestamp,
} from './constants';

function generateMarkdown(result: ConversationAnalysisResult): string {
  const lines: string[] = [];

  lines.push('# Анализ переписки');
  lines.push('');
  lines.push(`**Сообщений:** ${result.messagesAnalyzed} | **Участников:** ${result.participantsCount}`);
  if (result.periodStart && result.periodEnd) {
    lines.push(`**Период:** ${formatTimestamp(result.periodStart)} - ${formatTimestamp(result.periodEnd)}`);
  }
  lines.push('');

  lines.push('## Текущий статус');
  lines.push(`**Стадия:** ${STAGE_LABELS[result.currentState.stage] || result.currentState.stage}`);
  lines.push('');
  lines.push(result.currentState.summary);
  lines.push('');
  if (result.currentState.whatDone.length > 0) {
    lines.push('### Что сделано');
    result.currentState.whatDone.forEach((item) => lines.push(`- ${item}`));
    lines.push('');
  }
  if (result.currentState.waitingFor.length > 0) {
    lines.push('### Ожидаем');
    result.currentState.waitingFor.forEach((item) =>
      lines.push(`- [${OWNER_LABELS[item.who] || item.who}] ${item.what}`)
    );
    lines.push('');
  }

  if (result.pains.length > 0) {
    lines.push('## Боли и запросы');
    result.pains.forEach((pain) => {
      lines.push(`### ${pain.title}`);
      if (pain.details) lines.push(pain.details);
      if (pain.evidenceSnippets.length > 0) {
        lines.push('');
        pain.evidenceSnippets.forEach((s) => lines.push(`> "${s}"`));
      }
      lines.push('');
    });
  }

  if (result.openQuestions.length > 0) {
    lines.push('## Незакрытые вопросы');
    result.openQuestions.forEach((q) => {
      lines.push(`- [${OWNER_LABELS[q.owner] || q.owner}] ${q.question}`);
      if (q.deadline) lines.push(`  - Срок: ${q.deadline}`);
    });
    lines.push('');
  }

  if (result.alreadyDiscussed.length > 0) {
    lines.push('## Что уже обсуждали');
    result.alreadyDiscussed.forEach((item) => {
      lines.push(`- [${DISCUSSED_TYPE_LABELS[item.type] || item.type}] ${item.point}`);
    });
    lines.push('');
  }

  if (result.risks.length > 0) {
    lines.push('## Риски');
    result.risks.forEach((risk) => {
      const severity = risk.severity === 'high' ? 'ВЫСОКИЙ' : risk.severity === 'medium' ? 'СРЕДНИЙ' : 'НИЗКИЙ';
      lines.push(`- [${severity}] ${risk.description}`);
    });
    lines.push('');
  }

  if (result.keyMoments.length > 0) {
    lines.push('## Хронология');
    result.keyMoments.forEach((m) => {
      const prefix = m.at ? `[${m.at}] ` : '';
      lines.push(`- ${prefix}${m.point}`);
    });
    lines.push('');
  }

  return lines.join('\n');
}

export function ResultsPanel({
  job,
  result,
  error,
}: {
  job: AnalysisJob | null;
  result: ConversationAnalysisResult | null;
  error: string | null;
}): JSX.Element {
  // Empty state
  if (!job && !result && !error) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8">
        <div className="w-20 h-20 rounded-full bg-surface-elevated border border-border-subtle flex items-center justify-center mb-6">
          <svg
            className="w-10 h-10 text-text-secondary"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
            />
          </svg>
        </div>
        <h3 className="font-heading text-xl font-semibold text-text-primary mb-2">
          Загрузите переписку
        </h3>
        <p className="text-text-secondary text-center max-w-sm">
          Вставьте текст или загрузите файл переписки и запустите анализ
        </p>
      </div>
    );
  }

  // Progress state
  if (job && job.status !== 'DONE' && job.status !== 'FAILED') {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8">
        <Spinner size="lg" className="text-accent-brand mb-4" />
        <h3 className="font-heading text-lg font-semibold text-text-primary mb-2">
          {STEP_LABELS[job.step] || 'Обработка...'}
        </h3>
        <div className="w-64 h-2 bg-surface-subtle rounded-full overflow-hidden mb-2">
          <div
            className="h-full bg-accent-action transition-all duration-300"
            style={{ width: `${job.progressPct}%` }}
          />
        </div>
        <p className="text-text-secondary text-sm">
          {job.progressPct}%
          {job.totalChunks > 0 && ` (${job.processedChunks}/${job.totalChunks} частей)`}
        </p>
      </div>
    );
  }

  // Error state
  if (error || (job && job.status === 'FAILED')) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8">
        <div className="w-16 h-16 rounded-full bg-accent-danger/10 flex items-center justify-center mb-4">
          <svg
            className="w-8 h-8 text-accent-danger"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
            />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-text-primary mb-2">Ошибка анализа</h3>
        <p className="text-text-secondary text-sm text-center max-w-sm">
          {error || job?.error || 'Произошла неизвестная ошибка'}
        </p>
      </div>
    );
  }

  // Loading result
  if (!result) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Spinner size="lg" className="text-accent-brand" />
      </div>
    );
  }

  const handleCopyMarkdown = () => {
    const md = generateMarkdown(result);
    navigator.clipboard.writeText(md);
  };

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-4">
      {/* Export buttons */}
      <div className="flex justify-end gap-2">
        <Button variant="secondary" size="sm" onClick={handleCopyMarkdown}>
          <span className="flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184"
              />
            </svg>
            Копировать
          </span>
        </Button>
        <div className="relative group">
          <Button variant="ghost" size="sm" disabled>
            <span className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m.75 12l3 3m0 0l3-3m-3 3v-6m-1.5-9H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                />
              </svg>
              PDF
            </span>
          </Button>
          <div className="absolute bottom-full right-0 mb-2 px-2 py-1 text-xs bg-surface-subtle text-text-secondary rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            Скоро
          </div>
        </div>
      </div>

      {/* Meta */}
      <div className="flex flex-wrap gap-2 text-sm text-text-secondary">
        <span>Сообщений: {result.messagesAnalyzed}</span>
        <span>|</span>
        <span>Участников: {result.participantsCount}</span>
        {result.periodStart && result.periodEnd && (
          <>
            <span>|</span>
            <span>
              {formatTimestamp(result.periodStart)} - {formatTimestamp(result.periodEnd)}
            </span>
          </>
        )}
      </div>

      {/* 1. Current State */}
      <CollapsibleSection
        title="Текущий статус"
        badge={
          <span className="px-2 py-0.5 text-xs rounded-full bg-accent-brand/20 text-accent-brand">
            {STAGE_LABELS[result.currentState.stage] || result.currentState.stage}
          </span>
        }
      >
        <div className="space-y-3">
          <p className="text-text-primary">{result.currentState.summary}</p>
          {result.currentState.whatDone.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-text-secondary mb-1">Что сделано:</h4>
              <ul className="list-disc list-inside text-sm text-text-primary space-y-1">
                {result.currentState.whatDone.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>
          )}
          {result.currentState.waitingFor.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-text-secondary mb-1">Ожидаем:</h4>
              <ul className="space-y-1">
                {result.currentState.waitingFor.map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <span className="px-1.5 py-0.5 text-xs rounded bg-surface-subtle text-text-secondary">
                      {OWNER_LABELS[item.who] || item.who}
                    </span>
                    <span className="text-text-primary">{item.what}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </CollapsibleSection>

      {/* 2. Pains */}
      {result.pains.length > 0 && (
        <CollapsibleSection
          title="Боли и запросы"
          badge={
            <span className="px-2 py-0.5 text-xs rounded-full bg-accent-danger/20 text-accent-danger">
              {result.pains.length}
            </span>
          }
        >
          <div className="space-y-3">
            {result.pains.map((pain, i) => (
              <div key={i} className="pb-3 border-b border-border-subtle last:border-b-0 last:pb-0">
                <h4 className="font-medium text-text-primary">{pain.title}</h4>
                {pain.details && <p className="text-sm text-text-secondary mt-1">{pain.details}</p>}
                <EvidenceSnippets snippets={pain.evidenceSnippets} />
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* 3. Open Questions */}
      {result.openQuestions.length > 0 && (
        <CollapsibleSection
          title="Незакрытые вопросы"
          badge={
            <span className="px-2 py-0.5 text-xs rounded-full bg-accent-warning/20 text-accent-warning">
              {result.openQuestions.length}
            </span>
          }
        >
          <div className="space-y-3">
            {result.openQuestions.map((q, i) => (
              <div key={i} className="pb-3 border-b border-border-subtle last:border-b-0 last:pb-0">
                <div className="flex items-start gap-2">
                  <span
                    className={[
                      'px-1.5 py-0.5 text-xs rounded flex-shrink-0',
                      q.owner === 'us'
                        ? 'bg-accent-brand/20 text-accent-brand'
                        : q.owner === 'client'
                        ? 'bg-accent-success/20 text-accent-success'
                        : 'bg-surface-subtle text-text-secondary',
                    ].join(' ')}
                  >
                    {OWNER_LABELS[q.owner] || q.owner}
                  </span>
                  <span className="text-text-primary">{q.question}</span>
                </div>
                {q.deadline && (
                  <p className="text-xs text-text-secondary mt-1">Срок: {q.deadline}</p>
                )}
                <EvidenceSnippets snippets={q.evidenceSnippets} />
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* 4. Already Discussed */}
      {result.alreadyDiscussed.length > 0 && (
        <CollapsibleSection
          title="Что уже обсуждали"
          badge={
            <span className="px-2 py-0.5 text-xs rounded-full bg-accent-brand/20 text-accent-brand">
              {result.alreadyDiscussed.length}
            </span>
          }
        >
          <div className="space-y-3">
            {result.alreadyDiscussed.map((item, i) => (
              <div key={i} className="pb-3 border-b border-border-subtle last:border-b-0 last:pb-0">
                <div className="flex items-start gap-2">
                  <span className="px-1.5 py-0.5 text-xs rounded bg-surface-subtle text-text-secondary flex-shrink-0">
                    {DISCUSSED_TYPE_LABELS[item.type] || item.type}
                  </span>
                  <span className="text-text-primary">{item.point}</span>
                </div>
                <EvidenceSnippets snippets={item.evidenceSnippets} />
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* 5. Risks */}
      {result.risks.length > 0 && (
        <CollapsibleSection
          title="Риски"
          badge={
            <span className="px-2 py-0.5 text-xs rounded-full bg-accent-danger/20 text-accent-danger">
              {result.risks.length}
            </span>
          }
        >
          <div className="space-y-3">
            {result.risks.map((risk, i) => (
              <div key={i} className="pb-3 border-b border-border-subtle last:border-b-0 last:pb-0">
                <div className="flex items-start gap-2">
                  <span className={`px-1.5 py-0.5 text-xs rounded ${RISK_SEVERITY_COLORS[risk.severity]}`}>
                    {risk.severity === 'high' ? 'Высокий' : risk.severity === 'medium' ? 'Средний' : 'Низкий'}
                  </span>
                  <span className="text-text-primary">{risk.description}</span>
                </div>
                <EvidenceSnippets snippets={risk.evidenceSnippets} />
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* 6. Key Moments / Timeline */}
      {result.keyMoments.length > 0 && (
        <CollapsibleSection title="Хронология">
          <div className="relative pl-4 border-l-2 border-border-subtle space-y-3">
            {result.keyMoments.map((moment, i) => (
              <div key={i} className="relative">
                <div className="absolute -left-[21px] w-3 h-3 rounded-full bg-accent-action" />
                <div>
                  {moment.at && (
                    <span className="text-xs text-text-secondary">{moment.at}</span>
                  )}
                  <p className="text-text-primary">{moment.point}</p>
                </div>
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* Entities (collapsed by default) */}
      {(result.entities.money.length > 0 ||
        result.entities.dates.length > 0 ||
        result.entities.products.length > 0 ||
        result.entities.people.length > 0) && (
        <CollapsibleSection title="Извлечённые данные" defaultOpen={false}>
          <div className="space-y-3 text-sm">
            {result.entities.money.length > 0 && (
              <div>
                <h4 className="font-medium text-text-secondary mb-1">Суммы:</h4>
                <ul className="space-y-1">
                  {result.entities.money.map((m, i) => (
                    <li key={i} className="text-text-primary">
                      {m.value} — <span className="text-text-secondary">{m.context}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {result.entities.dates.length > 0 && (
              <div>
                <h4 className="font-medium text-text-secondary mb-1">Даты:</h4>
                <ul className="space-y-1">
                  {result.entities.dates.map((d, i) => (
                    <li key={i} className="text-text-primary">
                      {d.value} — <span className="text-text-secondary">{d.context}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {result.entities.products.length > 0 && (
              <div>
                <h4 className="font-medium text-text-secondary mb-1">Продукты/услуги:</h4>
                <div className="flex flex-wrap gap-1">
                  {result.entities.products.map((p, i) => (
                    <span key={i} className="px-2 py-0.5 bg-surface-subtle rounded text-text-primary text-xs">
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {result.entities.people.length > 0 && (
              <div>
                <h4 className="font-medium text-text-secondary mb-1">Люди:</h4>
                <div className="flex flex-wrap gap-1">
                  {result.entities.people.map((p, i) => (
                    <span key={i} className="px-2 py-0.5 bg-surface-subtle rounded text-text-primary text-xs">
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CollapsibleSection>
      )}
    </div>
  );
}
